import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import {
  TrophyIcon, ArrowSquareOutIcon, SpinnerGapIcon, LockKeyIcon, PlusIcon, CheckIcon, UsersThreeIcon, ShareNetworkIcon, RocketLaunchIcon,
} from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchLeaderboard, fetchTrackedWallets, addTrackedWallet, fetchSmartMoneyConfig, fetchRunnerLeaderboard, batchFollowBuilders } from "../lib/api";
import { FollowBuilderButton } from "../components/FollowBuilderButton";
import { useSeo } from "../lib/useSeo";
import { LeaderboardShareCard } from "../components/LeaderboardShareCard";

const short = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;
const scoreColor = (s) => (s >= 60 ? "#00E599" : s >= 35 ? "#F5A623" : "#FF3366");
const medal = (rank) => (rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null);

export default function Leaderboard() {
  const clientId = getClientId();
  const qc = useQueryClient();
  const [showShare, setShowShare] = useState(false);
  const [tab, setTab] = useState("wallets");

  useSeo({
    title: "Top Smart Money — Best Solana Wallets by On-Chain PnL | AlphaApeing",
    description: "Live leaderboard of the best-performing Solana meme-coin wallets, ranked by verified on-chain PnL, win rate and realized SOL profit. Find proven smart-money traders and copy their moves.",
  });

  const configQ = useQuery({ queryKey: ["smConfig"], queryFn: fetchSmartMoneyConfig });
  const boardQ = useQuery({ queryKey: ["leaderboard"], queryFn: fetchLeaderboard, refetchInterval: 120000 });
  const runnerQ = useQuery({ queryKey: ["runnerLeaderboard"], queryFn: () => fetchRunnerLeaderboard(50), refetchInterval: 300000, enabled: tab === "deployers" });
  const trackedQ = useQuery({ queryKey: ["trackedWallets", clientId], queryFn: () => fetchTrackedWallets(clientId) });

  const trackMut = useMutation({
    mutationFn: (address) => addTrackedWallet(clientId, address, ""),
    onSuccess: () => {
      toast.success("Added to your Smart Money watchlist");
      qc.invalidateQueries({ queryKey: ["trackedWallets", clientId] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to track wallet"),
  });

  const wallets = boardQ.data?.wallets || [];
  const trackedSet = new Set((trackedQ.data?.wallets || []).map((w) => w.address));
  const heliusConfigured = configQ.data?.heliusConfigured;
  const deployers = runnerQ.data?.deployers || [];
  const followTopMut = useMutation({
    mutationFn: () => batchFollowBuilders(clientId, deployers.slice(0, 10).map((d) => d.creator)),
    onSuccess: (res) => {
      qc.setQueryData(["builderFollows", clientId], { creators: res.creators });
      qc.invalidateQueries({ queryKey: ["provenBuilders"] });
      qc.invalidateQueries({ queryKey: ["runnerLeaderboard"] });
      toast.success(res.added > 0
        ? `Following ${res.added} new top deployer${res.added === 1 ? "" : "s"} — their launches will ping your Telegram`
        : "You already follow the whole top 10");
    },
    onError: () => toast.error("Couldn't follow — try again"),
  });
  const shortAddr = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;
  const fmtMcap = (n) => (n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : `$${n}`);

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
              <TrophyIcon size={24} weight="bold" />
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">LEADERBOARD</h1>
          </div>
          {wallets.length > 0 && tab === "wallets" && (
            <button
              data-testid="leaderboard-share-btn"
              onClick={() => setShowShare(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-neon/40 bg-neon/[0.06] text-neon hover:bg-neon/15 transition-colors"
            >
              <ShareNetworkIcon size={16} weight="bold" /> Share leaderboard
            </button>
          )}
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          {tab === "wallets" ? (
            <>The top-scoring wallets tracked across all of AlphaApeing, ranked by their <span className="text-neon font-semibold">PnL score</span>{" "}— win rate + realized/unrealized SOL. Find proven traders and copy them into your own Smart Money list.</>
          ) : (
            <>The builders behind the runners. Deployers ranked by how many of their launches hit <span className="text-neon font-semibold">$5M+ peak mcap</span> — the wallets with a real track record of shipping tokens that run.</>
          )}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10">
        <button
          data-testid="lb-tab-wallets"
          onClick={() => setTab("wallets")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "wallets" ? "border-neon text-neon" : "border-transparent text-muted-foreground hover:text-white"}`}
        >
          <TrophyIcon size={16} weight="bold" /> Smart Money
        </button>
        <button
          data-testid="lb-tab-deployers"
          onClick={() => setTab("deployers")}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "deployers" ? "border-neon text-neon" : "border-transparent text-muted-foreground hover:text-white"}`}
        >
          <RocketLaunchIcon size={16} weight="bold" /> Top Deployers
        </button>
      </div>

      {tab === "wallets" && (<>
      {!configQ.isLoading && !heliusConfigured && (
        <div className="border border-warn/30 bg-warn/5 p-4 flex gap-3">
          <LockKeyIcon size={20} className="text-warn shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">Helius isn't connected — the leaderboard is disabled until an API key is added.</p>
        </div>
      )}

      {boardQ.isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={28} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Crunching wallet PnL…</span>
        </div>
      ) : wallets.length === 0 ? (
        <div className="border border-white/10 bg-card py-24 flex flex-col items-center gap-4 text-center px-6">
          <UsersThreeIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm max-w-md">
            No ranked wallets yet. Track wallets on the Smart Money page — once they have closed trades, the best ones surface here.
          </p>
        </div>
      ) : (
        <div className="border border-white/10 bg-card overflow-x-auto">
          <table className="w-full border-collapse min-w-[720px]">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02]">
                {["#", "Wallet", "Score", "Win rate", "Total PnL", "W / L", "Tracked by", ""].map((h, i) => (
                  <th key={i} className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${i === 1 ? "text-left" : i === 7 ? "text-center" : "text-right"}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wallets.map((w, i) => {
                const rank = i + 1;
                const tracked = trackedSet.has(w.address);
                return (
                  <tr key={w.address} data-testid={`lb-row-${w.address}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-3 py-3 text-right font-mono text-sm w-12">
                      {medal(rank) || <span className="text-muted-foreground">{rank}</span>}
                    </td>
                    <td className="px-3 py-3">
                      <a href={`https://solscan.io/account/${w.address}`} target="_blank" rel="noreferrer" className="font-mono text-sm hover:text-neon flex items-center gap-1.5">
                        {w.label ? (
                          <span className="flex items-center gap-1.5">
                            <span className="font-heading font-bold not-italic">{w.label}</span>
                            {w.curated && <span className="text-[9px] font-mono px-1 py-0.5 bg-neon/10 border border-neon/30 text-neon">CURATED</span>}
                            <span className="text-muted-foreground text-xs">{short(w.address)}</span>
                          </span>
                        ) : (
                          short(w.address)
                        )}
                        <ArrowSquareOutIcon size={12} />
                      </a>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-mono font-bold text-lg" style={{ color: scoreColor(w.score) }}>{w.score}</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-sm">{w.winRate}%</td>
                    <td className="px-3 py-3 text-right font-mono text-sm" style={{ color: w.totalPnlSol >= 0 ? "#00E599" : "#FF3366" }}>
                      {w.totalPnlSol >= 0 ? "+" : ""}{w.totalPnlSol} SOL
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs">
                      <span className="text-neon">{w.wins}W</span> <span className="text-danger">{w.losses}L</span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-sm text-muted-foreground">{w.trackedBy}</td>
                    <td className="px-3 py-3 text-center">
                      {tracked ? (
                        <span className="inline-flex items-center gap-1 text-xs text-neon font-mono"><CheckIcon size={14} weight="bold" /> Tracked</span>
                      ) : (
                        <button
                          data-testid={`lb-track-${w.address}`}
                          onClick={() => trackMut.mutate(w.address)}
                          disabled={trackMut.isPending}
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-neon/40 text-neon text-xs font-semibold hover:bg-neon/10 transition-colors disabled:opacity-40"
                        >
                          <PlusIcon size={14} weight="bold" /> Track
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground text-center">
        PnL is SOL-denominated & approximate (avg-cost over recent swap history). Research aid, not financial advice.
      </p>
      </>)}

      {tab === "deployers" && (
        runnerQ.isLoading ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
            <SpinnerGapIcon size={28} className="animate-spin text-neon" />
            <span className="font-mono text-sm">Ranking deployers by runners…</span>
          </div>
        ) : deployers.length === 0 ? (
          <div className="border border-white/10 bg-card py-24 flex flex-col items-center gap-4 text-center px-6" data-testid="runner-lb-empty">
            <RocketLaunchIcon size={40} className="text-muted-foreground" />
            <p className="text-muted-foreground font-mono text-sm max-w-md">
              No ranked deployers yet. As tokens flow through the radar and hit $1M+ peak mcap, their builders surface here.
            </p>
          </div>
        ) : (
          <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Follow a deployer to pin their coins on Proven Builders and get an instant Telegram ping on every new mint.
            </p>
            <button
              data-testid="follow-top10-btn"
              onClick={() => followTopMut.mutate()}
              disabled={followTopMut.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-neon text-black hover:bg-neon/90 transition-colors disabled:opacity-50"
              title="Follow the entire top 10 in one tap — every future launch pings you instantly"
            >
              {followTopMut.isPending ? <SpinnerGapIcon size={14} className="animate-spin" /> : <CheckIcon size={14} weight="bold" />}
              Follow all top 10
            </button>
          </div>
          <div className="border border-white/10 bg-card overflow-x-auto" data-testid="runner-leaderboard">
            <table className="w-full border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02]">
                  {["#", "Deployer", "$5M+", "$1M+", "Tokens", "Hit rate", "Best runners", ""].map((h, i) => (
                    <th key={i} className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${i === 1 || i === 6 ? "text-left" : "text-right"}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deployers.map((d, i) => {
                  const rank = i + 1;
                  return (
                    <tr key={d.creator} data-testid={`runner-lb-row-${d.creator}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="px-3 py-3 text-right font-mono text-sm w-12">
                        {medal(rank) || <span className="text-muted-foreground">{rank}</span>}
                      </td>
                      <td className="px-3 py-3">
                        <a href={`https://solscan.io/account/${d.creator}`} target="_blank" rel="noreferrer" className="font-mono text-sm hover:text-neon flex items-center gap-1.5">
                          {shortAddr(d.creator)} <ArrowSquareOutIcon size={12} />
                        </a>
                        {d.followers > 0 && (
                          <span data-testid={`follower-count-${d.creator}`} className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-neon">
                            <UsersThreeIcon size={11} weight="fill" /> {d.followers} follower{d.followers === 1 ? "" : "s"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-lg text-neon">{d.strongRunnerCount}</td>
                      <td className="px-3 py-3 text-right font-mono text-sm">{d.runnerCount}</td>
                      <td className="px-3 py-3 text-right font-mono text-sm text-muted-foreground">{d.totalTokens}</td>
                      <td className="px-3 py-3 text-right font-mono text-sm">{Math.round((d.successRate || 0) * 100)}%</td>
                      <td className="px-3 py-3 text-left">
                        <div className="flex flex-wrap gap-1.5">
                          {(d.best || []).map((b, bi) => (
                            <span key={bi} className="font-mono text-[10px] px-2 py-0.5 border border-neon/25 bg-neon/[0.05] text-neon">
                              {b.symbol || "?"} · {fmtMcap(b.peakMcap)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <FollowBuilderButton creator={d.creator} size="sm" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground text-center">
            Ranked by strong runners ($5M+ peak mcap), then $1M+ runners. Based on AlphaApeing's token index — a track record signal, not a guarantee. Not financial advice.
          </p>
          </>
        )
      )}

      <LeaderboardShareCard wallets={wallets} open={showShare} onClose={() => setShowShare(false)} />
    </div>
  );
}
